import { RUNTIME_BACKEND_URL } from '../lib/backendUrl';
import { useEffect, useRef, useState } from 'react';
import { Button } from './ui/button';
import { Send, Loader2, Info, Smile } from 'lucide-react';

const BACKEND = RUNTIME_BACKEND_URL;

const QUICK_REPLIES = [
  { label: 'Tamam ✓', text: 'Tamam ✓' },
  { label: 'Teşekkürler 🙏', text: 'Teşekkürler 🙏' },
  { label: 'Ne zaman?', text: 'Ne zaman hazır olur? ⏰' },
  { label: 'Kargo no?', text: 'Kargo takip numarası var mı? 📦' },
  { label: 'Acil', text: 'Acil gerekiyor, hızlandırır mısınız? 🔥' },
];
const EMOJIS = ['👍','👏','❤️','🙏','🎉','🔥','✨','⏰','📦','✅','❌','💐','💍','🌸','🎂'];

export default function OrderChatPanel({ orderId, token, currentUserId, disabled = false }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const scrollRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    try {
      const r = await fetch(`${BACKEND}/api/print-orders/${orderId}/messages`, { headers });
      if (!r.ok) throw new Error();
      const data = await r.json();
      setMessages(Array.isArray(data) ? data : []);
    } catch {
      // sessizce geç
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!orderId) return;
    load();
    const iv = setInterval(load, 8000); // her 8sn yenile
    return () => clearInterval(iv);
  }, [orderId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const send = async (override) => {
    const t = (override !== undefined ? override : text).trim();
    if (!t) return;
    setSending(true);
    try {
      const r = await fetch(`${BACKEND}/api/print-orders/${orderId}/messages`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: t })
      });
      if (r.ok) {
        if (override === undefined) setText('');
        load();
      }
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="border rounded-xl bg-white overflow-hidden" data-testid="order-chat">
      <div className="bg-slate-50 border-b px-3 py-2 text-xs font-bold text-slate-600">
        Sohbet
      </div>

      <div ref={scrollRef} className="max-h-80 overflow-y-auto p-3 space-y-2 bg-slate-50/50">
        {loading ? (
          <div className="flex items-center justify-center py-4 text-slate-400 text-xs">
            <Loader2 className="h-4 w-4 animate-spin mr-1" />Yükleniyor...
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center text-xs text-slate-400 py-4">
            Henüz mesaj yok — ilk mesajı siz yazın
          </div>
        ) : messages.map(m => {
          const isSystem = m.sender_role === 'system';
          const isMine = m.sender_id === currentUserId;
          if (isSystem) {
            return (
              <div key={m.id} className="flex justify-center">
                <div className="bg-amber-50 border border-amber-200 rounded-full px-3 py-1 text-[11px] text-amber-700 flex items-center gap-1 max-w-[90%]">
                  <Info className="h-3 w-3 flex-shrink-0" />
                  <span>{m.text}</span>
                  <span className="text-amber-500 ml-1">· {new Date(m.at).toLocaleString('tr-TR', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })}</span>
                </div>
              </div>
            );
          }
          return (
            <div key={m.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[75%] rounded-2xl px-3 py-1.5 ${
                isMine ? 'bg-primary text-white' : 'bg-white border border-slate-200 text-slate-800'
              }`}>
                {!isMine && (
                  <div className={`text-[10px] font-bold mb-0.5 ${isMine ? 'text-primary-foreground/70' : 'text-primary'}`}>
                    {m.sender_name}
                  </div>
                )}
                <div className="text-sm whitespace-pre-wrap break-words">{m.text}</div>
                <div className={`text-[9px] mt-0.5 ${isMine ? 'text-white/70' : 'text-slate-400'} text-right`}>
                  {new Date(m.at).toLocaleString('tr-TR', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {!disabled && (
        <>
          {/* Quick replies */}
          {messages.length === 0 || messages[messages.length - 1]?.sender_id !== currentUserId ? (
            <div className="border-t bg-white px-2 py-1 flex gap-1 overflow-x-auto">
              {QUICK_REPLIES.map((qr, i) => (
                <button key={i} onClick={() => send(qr.text)}
                  className="flex-shrink-0 bg-slate-100 hover:bg-primary hover:text-white text-xs px-2 py-1 rounded-full transition whitespace-nowrap"
                  data-testid={`quick-reply-${i}`}>
                  {qr.label}
                </button>
              ))}
            </div>
          ) : null}

          {/* Emoji picker */}
          {showEmoji && (
            <div className="border-t bg-white p-2 flex flex-wrap gap-1">
              {EMOJIS.map((e, i) => (
                <button key={i} onClick={() => { setText(t => t + e); setShowEmoji(false); }}
                  className="text-xl hover:bg-slate-100 rounded p-1 transition">
                  {e}
                </button>
              ))}
            </div>
          )}

          <div className="border-t bg-white p-2 flex gap-1 items-end">
            <button onClick={() => setShowEmoji(s => !s)}
              className="p-2 text-slate-400 hover:text-amber-500 rounded"
              data-testid="emoji-toggle">
              <Smile className="h-5 w-5" />
            </button>
            <textarea value={text} onChange={e => setText(e.target.value)} onKeyDown={onKey}
              rows={1} placeholder="Mesajınızı yazın..."
              className="flex-1 resize-none rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:outline-none focus:border-primary"
              data-testid="chat-input" />
            <Button size="sm" onClick={() => send()} disabled={sending || !text.trim()} data-testid="chat-send-btn">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
